/**
 * turnMapSelection — what the map should highlight during the attack / fortify
 * phases, as one pure function shared by AttackUI and FortifyUI.
 *
 * Pulled out of the two phase components because the interesting case is a
 * three-way interaction between local selection state, server state
 * (`lastConquest`) and the map's highlight props, and getting it wrong is
 * invisible to the type system — it just paints the wrong territory.
 *
 * Three states:
 *
 *   1. FREE FORTIFY PENDING (attack phase only). A conquest has landed and the
 *      player owes the engine a "move armies forward / skip" answer. The map is
 *      inert until they give it, so the ONLY thing it may highlight is the
 *      conquest pair — the source and the territory just taken. Anything else
 *      is the board answering a question nobody asked.
 *
 *      This is the regression the function exists to prevent: the attack UI
 *      kept its own sticky source selected through the conquest, and the
 *      auto-pick-the-only-target effect then re-selected a NEW enemy behind the
 *      dialog. That enemy rendered with the secondary selection fill, so a
 *      territory that had nothing to do with the free fortify — and on a map
 *      with one-way borders could be most of the board away — showed up greyed
 *      next to it.
 *
 *   2. A SOURCE IS SELECTED, NO TARGET YET. Highlight the source and preview
 *      everywhere it could act. This is the only state the preview appears in:
 *      it answers "where can I go from here", which is a question you only have
 *      while you are still choosing.
 *
 *   3. BOTH ENDS PICKED. Source + destination, and the preview goes away. The
 *      engagement is decided and the popup below the map now owns the decision;
 *      leaving a dozen rings up would keep advertising alternatives to a choice
 *      that has already been made.
 *
 *   4. NOTHING SELECTED. The clickable set is every legal source (none once the
 *      attack cap is spent). There is nothing to preview yet.
 */

/** The conquest pair the engine leaves pending for a post-conquest free fortify. */
export interface ConquestPair {
  fromTerritoryId: string
  toTerritoryId: string
}

export interface TurnMapSelectionInput {
  /**
   * The pending conquest pair, but ONLY while the free-fortify panel is
   * actually on screen (i.e. the source still has an army it can move). Null
   * during the fortify phase, which has no such interrupt.
   */
  freeFortify?: ConquestPair | null
  selectedFromId: string | null
  selectedToId: string | null
  /** Every territory the player could pick as a source right now. */
  sourceCandidateIds: readonly string[]
  /** Every territory the SELECTED source could act on. Empty with no source. */
  targetIds: readonly string[]
  /** Attack/fortify cap spent — no source is selectable. */
  capReached: boolean
}

export interface TurnMapSelection {
  selectedTerritoryId?: string
  secondaryTerritoryId?: string
  /** Clickable set for this phase (drives the web hover cursor). */
  validTargetIds: string[]
  /** Marching-ants preview ring — where the selection could act. */
  previewTargetIds: string[]
}

export function turnMapSelection(input: TurnMapSelectionInput): TurnMapSelection {
  const { freeFortify, selectedFromId, selectedToId, sourceCandidateIds, targetIds, capReached } =
    input

  if (freeFortify) {
    return {
      selectedTerritoryId: freeFortify.fromTerritoryId,
      secondaryTerritoryId: freeFortify.toTerritoryId,
      validTargetIds: [],
      previewTargetIds: [],
    }
  }

  if (!selectedFromId) {
    return {
      validTargetIds: capReached ? [] : [...sourceCandidateIds],
      previewTargetIds: [],
    }
  }

  return {
    selectedTerritoryId: selectedFromId,
    ...(selectedToId ? { secondaryTerritoryId: selectedToId } : {}),
    validTargetIds: [...targetIds],
    // Only while the second end is still open — see state 3 above.
    previewTargetIds: selectedToId ? [] : [...targetIds],
  }
}
